// This code has been partially generated by an AI.
import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ToastService } from '@app/core/services/toast.service';
import { DialogService } from '@app/core/services/dialog.service';
import {
  EmployeeBasicDto,
  EmployeeConfirmationDto,
  EmployeeDto,
  EmployeepictureDto,
  EmployeeWithExpiredDto,
  StatusGroup,
} from '@app/_api/dto';
import { BaseApi } from '@app/core/services/api/base.api';
import { UUID } from '@app/_api/identifyable';
import { EmployeeCore } from '@app/pages/profile/skill-person-matches/match-list/match-list.types';

export type Subordinate = {
  uuid: UUID;
  firstname: string;
  lastname: string;
  statusGroups: string[];
  visibilityGroups: string[];
};

@Service()
export class EmployeeApi extends BaseApi {
  constructor() {
    const http = inject(HttpClient);
    const toastService = inject(ToastService);
    const dialog = inject(DialogService);
    super(http, '/employee', toastService, dialog);
  }

  getSubordinatesForCurrentUser(): Observable<Subordinate[]> {
    return super.GET<Subordinate[]>('/subordinates');
  }

  hasCurrentUserSubordinates(): Observable<boolean> {
    return super.GET<boolean>('/has-subordinates');
  }

  getAllEmployees(): Observable<EmployeeDto[]> {
    return this.handleHttp<EmployeeDto[]>(this.http.get<EmployeeDto[]>(this.buildPath('/all')));
  }

  getAllStatusGroups(): Observable<StatusGroup[]> {
    return this.handleHttp<StatusGroup[]>(this.http.get<StatusGroup[]>(this.buildPath('/statusgroups/all')));
  }

  updateVisibilityGroups(visibilityGroups: StatusGroup[]): Observable<EmployeeDto> {
    return this.handleHttp<EmployeeDto>(this.http.patch<EmployeeDto>(this.buildPath('/visibilitygroups/update'), visibilityGroups), {
      success: 'Sichtbarkeit erfolgreich aktualisiert',
      error: 'Fehler beim Aktualisieren der Sichtbarkeit',
    });
  }

  getPictureForEmployee(uuid: string, imageResolution: string, version: string = '1'): Observable<EmployeepictureDto> {
    return this.handleHttp<EmployeepictureDto>(
      this.http.get<EmployeepictureDto>(this.buildPath(`/picture/${uuid}`), { params: { imageResolution, v: version } }),
    );
  }

  uploadPicture(picture: Blob): Observable<void> {
    const body = new FormData();
    body.append('file', picture, 'profile.png');
    return super.PUT<void>('/picture', body, undefined, {
      success: 'Profilbild erfolgreich gespeichert',
      error: 'Das Profilbild konnte nicht gespeichert werden. Erlaubt sind JPEG/PNG bis 240 × 240 Pixel und 256 KiB.',
    });
  }

  deletePicture(): Observable<void> {
    return super.DELETE<void>('/picture', undefined, {
      success: 'Lokales Profilbild entfernt',
      error: 'Das lokale Profilbild konnte nicht entfernt werden.',
    });
  }

  getEmployeeById(employeeId: UUID): Observable<EmployeeBasicDto> {
    return this.handleHttp<EmployeeBasicDto>(this.http.get<EmployeeBasicDto>(this.buildPath(`/get/byId/${employeeId}`)));
  }

  confirm(): Observable<EmployeeDto> {
    return this.handleHttp<EmployeeDto>(this.http.patch<EmployeeDto>(this.buildPath('/confirm'), null), {
      success: 'Skills und Zertifikate erfolgreich bestätigt',
      error: 'Fehler beim Bestätigen der Skills und Zertifikate',
    });
  }

  areAllAssignmentsConfirmedForEmployee(): Observable<EmployeeConfirmationDto> {
    return this.handleHttp<EmployeeConfirmationDto>(this.http.get<EmployeeConfirmationDto>(this.buildPath('/confirmationState')));
  }

  /**
   * Not under /employee, hence no buildPath.
   * @returns the current employee, created on first login
   */
  registerCurrentEmployee(): Observable<EmployeeDto> {
    return this.handleHttp<EmployeeDto>(this.http.get<EmployeeDto>('/employee-registered'));
  }

  fetchEmployeesByCertificateId(certificateId: UUID): Observable<EmployeeWithExpiredDto[]> {
    return super.GET(`/certificateAssignments/${certificateId}`);
  }

  getEmployeesVisibleForMe(): Observable<EmployeeCore[]> {
    return this.handleHttp<EmployeeCore[]>(this.http.get<EmployeeCore[]>(this.buildPath(`/visible`)));
  }
}
